// Shared component (TS-2.5.03). A generic, focus-managed, scroll-locking modal dialog for a
// larger READ-ONLY view of something already shown compactly elsewhere - first consumer is the
// roadmap's detail-card Expand affordance (decisions.md D-280). Generalizes ConfirmDialog's own
// tested focus-management contract (open/Escape/Tab-trap/focus-return, D-241) to ARBITRARY
// children rather than a fixed two-button footer, because a "larger view" of a project can hold an
// unbounded number of focusable things (a milestone list, an "Open project" link) that a
// two-ref trap cannot enumerate ahead of time.
//
// ConfirmDialog itself is DELIBERATELY NOT refactored onto this component in this batch (D-280) -
// it is small, its own test suite is green and stable, and unifying the two is a real but separate
// change (recorded as deferred future work, not silently done here as a drive-by).
//
// Differences from ConfirmDialog beyond the variable content:
//   - role="dialog" (not "alertdialog"): this is an information view a reader opened voluntarily,
//     not an interruption demanding a decision (aria-modal="true" still applies either way).
//   - Initial focus lands on the dialog's own close button (always present, always first in DOM
//     order) rather than a caller-chosen ref - simpler for a content shape that varies per call
//     site, and a safe, discoverable place to land regardless of what the body contains.
//   - The Tab trap is computed live off the CURRENT set of focusable descendants (queried on each
//     Tab keypress) rather than two fixed refs, since the body's focusable set is caller-defined
//     and can even be empty (nothing to trap between but the close button itself).
//   - Scroll-locks the background (`document.body.style.overflow`) while open, restored on close -
//     ConfirmDialog never needed this because it never covers scrollable page content for long.
import { useEffect, useRef, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import "./modal.css";

export interface ModalProps {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  readonly closeLabel: string;
  readonly children: ReactNode;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ open, title, onClose, closeLabel, children }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<Element | null>(null);

  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      closeRef.current?.focus();

      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }

    if (triggerRef.current instanceof HTMLElement) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
    return undefined;
  }, [open]);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== "Tab" || !dialogRef.current) return;

    const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;

    if (event.shiftKey) {
      if (document.activeElement === first || !dialogRef.current.contains(document.activeElement)) {
        event.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // Backdrop click closes (owner request - the modern light-dismiss expectation, and safe here
  // because this modal is READ-ONLY by contract: there is never unsaved work to lose). Guarded on
  // target === currentTarget so a click INSIDE the dialog that bubbles up (or a drag that releases
  // over the backdrop) never dismisses; Escape and the × remain the keyboard/explicit paths.
  function handleBackdropClick(event: MouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) onClose();
  }

  if (!open) return null;

  return (
    <div className="eg-modal__overlay" onClick={handleBackdropClick}>
      <div
        ref={dialogRef}
        className="eg-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="eg-modal-title"
        onKeyDown={handleKeyDown}
      >
        <div className="eg-modal__header">
          <h2 id="eg-modal-title">{title}</h2>
          <button type="button" ref={closeRef} className="eg-modal__close" onClick={onClose} aria-label={closeLabel}>
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="eg-modal__body">{children}</div>
      </div>
    </div>
  );
}
